import { TOKEN_CONFIG } from "@/config";
import type { TokenId } from "@/types";
import { AssetIcon, safeAssetIconName, Tooltip } from "@/components";
import { memo, useMemo, type ReactNode } from "react";
import Big from "big.js";
import clsx from "clsx";

interface TokenAmountProps {
    amount?: string | number | ReactNode;
    tokenId: TokenId;
    size?: "sm" | "md" | "lg";
    decimals?: number;
    className?: string;
    suffix?: string;
    extra_desc?: string;
    slotProps?: {
        amount?: {
            className?: string;
        };
        symbol?: {
            className?: string;
        };
    };
}

export const TokenAmount = memo(function TokenAmount({
    amount,
    tokenId,
    size = "md",
    className,
    suffix = "",
    extra_desc = "",
    slotProps: { amount: amountSlotProps = {}, symbol: symbolSlotProps = {} } = {},
}: TokenAmountProps) {
    const sizeClasses = clsx({
        "text-[12px]": size === "sm",
        "text-[14px]": size === "md",
        "text-4xl": size === "lg",
    });

    const tokenConfig = TOKEN_CONFIG[tokenId];
    const displaySymbol = tokenConfig.symbol;

    const bAmount = useMemo(() => (typeof amount === "number" ? Big(amount).toFixed(6, 0) : amount), [amount]);

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <span className={`${sizeClasses} text-dark ${amountSlotProps.className}`}>{bAmount}</span>

            <div className="inline-flex items-center gap-2">
                <Tooltip title={tokenConfig.name + extra_desc} arrow placement="top">
                    <span>
                        <AssetIcon
                            name={safeAssetIconName(tokenId)}
                            size={size === "sm" ? 16 : 20}
                            className={`w-[24px] ${symbolSlotProps.className}`}
                        />
                    </span>
                </Tooltip>
                <span className={`${sizeClasses} text-dark min-w-[30px] text-right ${symbolSlotProps.className}`}>
                    {displaySymbol + suffix}
                </span>
            </div>
        </div>
    );
});

TokenAmount.displayName = "TokenAmount";
